import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";
import { linkUser, requestPush } from "@/lib/onesignal";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useLive } from "@/context/Realtime";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

function useNotifications() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/notifications");
      setData(d);
    } catch (e) {
      setData((x) => x);
    }
  }, []);
  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [load]);
  useLive((e) => {
    if (e.type === "notification" || e.type === "reconnect") load();
  });
  return [data, load];
}

export default function NotificationBell() {
  const { user, setMode, settings } = useAuth();
  const [data, load] = useNotifications();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const appId = settings.onesignal_app_id;
  useEffect(() => {
    const run = () => linkUser(appId, user);
    run();
    const onVis = () => { if (document.visibilityState === "visible") run(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [appId, user.id, user.is_worker, user.address?.province, user.address?.regency]); // eslint-disable-line react-hooks/exhaustive-deps

  const onOpen = (v) => {
    setOpen(v);
    if (v) requestPush(appId);
  };
  const go = async (n) => {
    setOpen(false);
    if (!n.read) await api.post(`/notifications/${n.id}/read`).catch(() => {});
    if (n.role === "worker" && user.is_worker) setMode("worker");
    if (n.role === "user") setMode("user");
    load();
    if (n.link) nav(n.link);
  };
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 grid place-items-center transition-colors">
          <Bell className="w-4 h-4 text-slate-700" />
          {data.unread > 0 && <span data-testid="notification-unread-count" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center">{data.unread > 9 ? "9+" : data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <p className="font-display font-bold">Notifikasi</p>
          {data.unread > 0 && <button data-testid="notification-read-all" onClick={readAll} className="text-xs font-bold text-emerald-700 flex items-center gap-1"><CheckCheck className="w-3.5 h-3.5" />Tandai dibaca</button>}
        </div>
        <div data-testid="notification-list" className="max-h-96 overflow-y-auto divide-y">
          {data.items.length === 0 && <p className="text-sm text-slate-500 text-center py-8">Belum ada notifikasi</p>}
          {data.items.map((n) => (
            <button key={n.id} data-testid={`notification-item-${n.id}`} onClick={() => go(n)} className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors ${n.read ? "" : "bg-emerald-50/60"}`}>
              <div className="flex items-start gap-2">
                {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-emerald-500 shrink-0" />}
                <div className="min-w-0">
                  <p className="text-sm font-bold">{n.title}</p>
                  <p className="text-xs text-slate-600 line-clamp-2">{n.body}</p>
                  {n.image && <img src={n.image} alt="" className="mt-1.5 w-full max-h-32 object-cover rounded-lg" />}
                  <p className="text-[10px] text-slate-400 mt-0.5">{n.role === "worker" ? "Worker · " : n.role === "user" ? "Pengguna · " : ""}{fmtDate(n.created_at)}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
